import { useState, useEffect, useRef } from "react";
import type { ComponentType } from "react";
import React from "react";
import * as ReactJsxRuntime from "react/jsx-runtime";
import * as Remotion from "remotion";

// Expose React and Remotion globally so dynamically loaded components can use them
(window as unknown as Record<string, unknown>).__MOTIONFORGE_REACT__ = React;
(window as unknown as Record<string, unknown>).__MOTIONFORGE_REACT_JSX__ = ReactJsxRuntime;
(window as unknown as Record<string, unknown>).__MOTIONFORGE_REMOTION__ = Remotion;

/**
 * Transforms raw TSX/JS component code into a browser-loadable Blob URL.
 *
 * Replaces bare import specifiers (react, remotion) with references to the
 * globally available modules that are already bundled in the main app.
 */
function transformForBrowser(jsCode: string): string {
  // Replace import statements with global references
  let transformed = jsCode;

  // Replace: import { X, Y } from "remotion" / 'remotion'
  transformed = transformed.replace(
    /import\s*\{([^}]+)\}\s*from\s*["']remotion["'];?/g,
    "const {$1} = window.__MOTIONFORGE_REMOTION__;"
  );

  // Replace: import React from "react" / 'react'
  transformed = transformed.replace(
    /import\s+React\s+from\s*["']react["'];?/g,
    "const React = window.__MOTIONFORGE_REACT__;"
  );

  // Replace: import { X } from "react" / 'react'
  transformed = transformed.replace(
    /import\s*\{([^}]+)\}\s*from\s*["']react["'];?/g,
    "const {$1} = window.__MOTIONFORGE_REACT__;"
  );

  // Replace: import { jsx, jsxs, jsxDEV, Fragment } from "react/jsx-runtime" or "react/jsx-dev-runtime"
  transformed = transformed.replace(
    /import\s*\{([^}]+)\}\s*from\s*["']react\/jsx-(?:dev-)?runtime["'];?/g,
    "const {$1} = window.__MOTIONFORGE_REACT_JSX__;"
  );

  // Replace: import { Img } from "@remotion/media-utils" or similar
  transformed = transformed.replace(
    /import\s*\{([^}]+)\}\s*from\s*["']@remotion\/[^"']+["'];?/g,
    "const {$1} = window.__MOTIONFORGE_REMOTION__;"
  );

  // Replace TypeScript interface blocks (not valid JS)
  transformed = transformed.replace(
    /interface\s+\w+\s*\{[^}]*\}/g,
    ""
  );

  // Remove "export default function X" -> "function X"
  transformed = transformed.replace(
    /export\s+default\s+function\s+(\w+)/,
    "function $1"
  );

  // Find the default export name BEFORE removing export statements.
  // Bun outputs: var _hash_default = ComponentName; export { _hash_default as default };
  // Or: export { ComponentName as default };
  const defaultExportMatch = transformed.match(
    /var\s+(\w+)\s*=\s*(\w+)\s*;\s*export\s*\{[^}]*as\s+default[^}]*\}/
  );
  const directExportMatch = transformed.match(
    /export\s*\{\s*(\w+)\s+as\s+default\s*\}/
  );

  // Remove "export { X as default }" or "export { X as default };" at end of file
  transformed = transformed.replace(
    /export\s*\{[^}]*\}\s*;?/g,
    ""
  );

  // Remove any remaining "export default X;"
  transformed = transformed.replace(
    /export\s+default\s+\w+\s*;?/g,
    ""
  );

  // Determine the component name from multiple patterns:
  let funcName = "Component";

  if (defaultExportMatch?.[2]) {
    funcName = defaultExportMatch[2];
  } else if (directExportMatch?.[1]) {
    funcName = directExportMatch[1];
  } else {
    const funcMatch = transformed.match(/function\s+(\w+)\s*\(/);
    const varMatch = transformed.match(/var\s+(\w+)\s*=\s*(?:\(|function)/);
    funcName = funcMatch?.[1] ?? varMatch?.[1] ?? "Component";
  }

  // Assign to global so we can pick it up
  transformed += `\nwindow.__MOTIONFORGE_COMPONENT__ = ${funcName};`;

  return transformed;
}

/**
 * Dynamically loads a compiled component from the backend's compile endpoint.
 * The backend compiles TSX to JS via Bun, then we transform the output
 * to use our globally-available React/Remotion instead of bare imports.
 */
export function useDynamicComponent(
  moduleUrl: string | null,
): ComponentType<Record<string, unknown>> | null {
  const [Component, setComponent] =
    useState<ComponentType<Record<string, unknown>> | null>(null);
  // Track which URL the current Component was loaded from. When moduleUrl
  // changes, we return null until the new module loads — this prevents the
  // error boundary from rendering the STALE component (which may throw)
  // while the new one is being fetched.
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const urlRef = useRef(moduleUrl);

  useEffect(() => {
    urlRef.current = moduleUrl;

    if (!moduleUrl) {
      setComponent(null);
      setLoadedUrl(null);
      setError(null);
      return;
    }

    let cancelled = false;

    async function loadModule(url: string) {
      try {
        setError(null);

        // Fetch the compiled JS from the backend
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch module: ${response.status}`);
        }

        let jsCode = await response.text();

        console.log("[useDynamicComponent] Fetched module, size:", jsCode.length);
        console.log("[useDynamicComponent] Globals available:", {
          react: !!(window as unknown as Record<string, unknown>).__MOTIONFORGE_REACT__,
          jsx: !!(window as unknown as Record<string, unknown>).__MOTIONFORGE_REACT_JSX__,
          remotion: !!(window as unknown as Record<string, unknown>).__MOTIONFORGE_REMOTION__,
        });

        // Transform bare imports to use our global references
        jsCode = transformForBrowser(jsCode);

        console.log("[useDynamicComponent] Transformed, first 300:", jsCode.slice(0, 300));

        // Wrap in try/catch so we get error details if the component code fails
        const wrappedCode = `try {\n${jsCode}\n} catch(e) { console.error("[MotionForge component error]", e); window.__MOTIONFORGE_COMPONENT_ERROR__ = e.message; }`;

        // Execute via Function() to avoid script tag async issues
        try {
          const fn = new Function(wrappedCode);
          fn();
        } catch (execErr) {
          console.error("[useDynamicComponent] execution error:", execErr);
          throw new Error(`Component execution failed: ${execErr}`);
        }

        // Check for component error
        const compError = (window as unknown as Record<string, unknown>).__MOTIONFORGE_COMPONENT_ERROR__ as string | undefined;
        if (compError) {
          delete (window as unknown as Record<string, unknown>).__MOTIONFORGE_COMPONENT_ERROR__;
          throw new Error(`Component error: ${compError}`);
        }

        if (cancelled) return;

        // Grab the component from the global
        const comp = (window as unknown as Record<string, unknown>).__MOTIONFORGE_COMPONENT__ as
          | ComponentType<Record<string, unknown>>
          | undefined;

        if (comp) {
          setComponent(() => comp);
          setLoadedUrl(url);
          // Clean up global
          delete (window as unknown as Record<string, unknown>).__MOTIONFORGE_COMPONENT__;
        } else {
          throw new Error("Component not found after script execution");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[useDynamicComponent]", err);
        setError(
          err instanceof Error ? err : new Error("Failed to load module"),
        );
        setComponent(null);
      }
    }

    void loadModule(moduleUrl);

    return () => {
      cancelled = true;
    };
  }, [moduleUrl]);

  if (error) {
    console.error("[useDynamicComponent] Error:", error.message);
  }

  // Always return the latest known-good Component, even when moduleUrl
  // has changed and the new one is still loading. This keeps the Player
  // visible across recompiles (no collapse → no layout shift → no
  // scroll jump). If the previous component is stale and throws, the
  // PlayerErrorBoundary catches it; once the new module finishes loading,
  // setComponent swaps in the fresh build atomically.
  //
  // Earlier behavior returned null during the loading gap. The intent
  // was to avoid showing a stale-throwing component, but the cost was
  // the entire Player area collapsing on every keystroke-debounced
  // recompile, which yanked the page scroll toward the top.
  return Component;
}
